import { Badge, HelpTooltip, ProgressBar } from "./UI";
import { Lock, Unlock, Clock, CircleDashed } from "lucide-react";

export const DEPT_ASSESSMENT_MODULES = [
  { id: "gap", key: "gap", title: "Gap Assessment" },
  { id: "pia", key: "pia", title: "Privacy Impact Assessment" },
  { id: "tprm", key: "tprm", title: "Third-Party Risk (TPRM)" },
  { id: "ropa", key: "ropa", title: "ROPA" },
];

export function assessmentStatusLabel(status) {
  if (status === "submitted") return "Submitted";
  if (status === "in_progress") return "In progress";
  return "Pending";
}

export function formatDeptProgress(row) {
  if (!row) return "—";
  const pct = row.progress_pct;
  const answered = row.progress_answered;
  const total = row.progress_total;
  if (answered != null && total) return `${pct ?? 0}% (${answered}/${total})`;
  if (pct != null) return `${pct}%`;
  return "—";
}

export function deptSerialNo(row, index = 0) {
  if (!row) return "—";
  return row.department_code || String(index + 1);
}

export function deptDisplayName(row) {
  if (!row) return "—";
  return row.department_name || "—";
}

function StatusBadge({ status }) {
  if (status === "submitted") return <Badge variant="success" title="This department has submitted the assessment.">Submitted</Badge>;
  if (status === "in_progress") return <Badge variant="warning" title="This department has started the assessment and has not submitted it.">In progress</Badge>;
  return <Badge variant="inactive" title="This department has not started the assessment.">Pending</Badge>;
}

function DeptNameList({ rows, empty }) {
  if (!rows?.length) {
    return <p className="text-sm text-slate-500">{empty}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {rows.map((d) => (
        <span
          key={d.department_id}
          className="inline-flex items-center text-xs font-medium text-slate-700 bg-white/80 border border-slate-200/80 rounded-full px-2.5 py-1"
        >
          {deptDisplayName(d)}
        </span>
      ))}
    </div>
  );
}

export function DepartmentFollowUpLists({ payload, compact }) {
  const pending = payload?.pending_departments || [];
  const inProgress = payload?.in_progress_departments || [];
  return (
    <div className={`grid ${compact ? "sm:grid-cols-2" : "lg:grid-cols-2"} gap-3`}>
      <div className="rounded-xl border border-amber-200/70 bg-amber-50/50 px-3.5 py-3">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-800 mb-2">
          <CircleDashed size={12} />
          Pending ({pending.length})
          <HelpTooltip content="Departments that have not started this assessment." size={12} position="bottom" />
        </p>
        <DeptNameList rows={pending} empty="No departments pending." />
      </div>
      <div className="rounded-xl border border-blue-200/70 bg-blue-50/50 px-3.5 py-3">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-800 mb-2">
          <Clock size={12} />
          In progress ({inProgress.length})
          <HelpTooltip content="Departments that have started this assessment and have not submitted it." size={12} position="bottom" />
        </p>
        <DeptNameList rows={inProgress} empty="No departments currently in progress." />
      </div>
    </div>
  );
}

export function DepartmentAssessmentTable({ payload, loading, formatTimestamp }) {
  const rows = payload?.departments || [];
  if (loading) {
    return (
      <div className="mt-3 space-y-2 animate-pulse">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-9 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 rounded-lg" />
        ))}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <p className="mt-3 text-sm text-slate-500 py-6 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
        No departments configured yet.
      </p>
    );
  }
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200/80 ring-1 ring-slate-900/[0.03]">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] text-slate-500 uppercase tracking-[0.12em] bg-gradient-to-r from-slate-50/80 to-blue-50/40">
            {[
              { label: "S.No", tip: "Department code, or a row number when no code is set.", className: "w-14" },
              { label: "Department", tip: "The department this assessment belongs to." },
              { label: "Status", tip: "Submitted, in progress, or not started." },
              { label: "Score", tip: "Assessment score for this department." },
              { label: "Submitted", tip: "When this department submitted the assessment." },
              { label: "Lock", tip: "Locked means the submitted assessment can no longer be edited." },
              { label: "Progress", tip: "How much of the assessment has been answered.", className: "min-w-[140px]" },
            ].map((col) => (
              <th key={col.label} className={`px-4 py-3 font-semibold ${col.className || ""}`}>
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  <HelpTooltip content={col.tip} size={11} position="bottom" />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.department_id}
              className="border-t border-slate-100/80 hover:bg-blue-50/25 transition-colors duration-150"
            >
              <td className="px-4 py-3 tabular-nums text-slate-500">{deptSerialNo(row, index)}</td>
              <td className="px-4 py-3 font-medium text-slate-800">{deptDisplayName(row)}</td>
              <td className="px-4 py-3">
                <StatusBadge status={row.status} />
              </td>
              <td
                className="px-4 py-3 font-bold tabular-nums text-slate-900"
                style={{ fontFamily: "Sora, sans-serif" }}
              >
                {row.score != null ? `${row.score}%` : "—"}
              </td>
              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                {formatTimestamp ? formatTimestamp(row.submitted_at) : row.submitted_at || "—"}
              </td>
              <td className="px-4 py-3">
                {row.is_locked ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                    <Lock size={12} /> Locked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                    <Unlock size={12} /> Unlocked
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2 min-w-[120px]">
                  <ProgressBar
                    value={row.progress_pct ?? 0}
                    color={
                      row.status === "submitted"
                        ? "emerald"
                        : row.status === "in_progress"
                          ? "amber"
                          : "primary"
                    }
                  />
                  <span className="text-[11px] tabular-nums text-slate-500 whitespace-nowrap">
                    {formatDeptProgress(row)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DepartmentAssessmentBlock({
  title,
  payload,
  loading,
  formatTimestamp,
  compact = false,
}) {
  const summary = payload?.summary || {};
  const cycleName = payload?.cycle?.name;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 font-display">{title}</h3>
          {cycleName && (
            <p className="text-[11px] text-slate-500 mt-0.5">Cycle: {cycleName}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="success" title="Departments that have submitted this assessment.">{loading ? "…" : (summary.submitted ?? 0)} submitted</Badge>
          <Badge variant="warning" title="Departments that have started and have not submitted.">{loading ? "…" : (summary.in_progress ?? 0)} in progress</Badge>
          <Badge variant="inactive" title="Departments that have not started this assessment.">{loading ? "…" : (summary.pending ?? 0)} pending</Badge>
        </div>
      </div>
      {!compact && (
        <DepartmentAssessmentTable
          payload={payload}
          loading={loading}
          formatTimestamp={formatTimestamp}
        />
      )}
      {!loading && <DepartmentFollowUpLists payload={payload} compact={compact} />}
    </div>
  );
}

export function DepartmentAssessmentsSection({
  showModule,
  reportData,
  loading,
  formatTimestamp,
  compact = false,
  className = "",
}) {
  const modules = DEPT_ASSESSMENT_MODULES.filter((m) => showModule?.(m.id));
  if (!modules.length) return null;
  return (
    <div
      className={`relative bg-white/60 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg ring-1 ring-slate-900/[0.04] overflow-hidden ${className}`}
    >
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent" />
      <div className="px-5 py-4 border-b border-slate-100/80 bg-gradient-to-r from-slate-50/70 via-white/50 to-blue-50/30">
        <p className="text-[10px] text-slate-500 uppercase tracking-[0.14em] font-semibold">
          Department assessments
        </p>
        <h2 className="text-sm font-semibold text-slate-900 font-display mt-0.5 inline-flex items-center gap-1">
          {modules.length === 1
            ? `${modules[0].title} by department`
            : "Department assessments in this data scope"}
          <HelpTooltip
            content={
              compact
                ? "How many departments have submitted, are still working, or have not started. The detailed report adds scores and dates."
                : "Status, score, submission date, lock, and progress for every department in this data scope."
            }
            size={13}
            position="bottom"
            maxWidth={320}
          />
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {compact
            ? "Counts only — submitted, in progress, and still pending for modules in this data scope."
            : "Every department: status, score, submission date, lock, and progress."}
        </p>
      </div>
      <div className="px-5 py-4 space-y-8">
        {modules.map((mod, idx) => (
          <div
            key={mod.key}
            className={idx > 0 ? "pt-6 border-t border-slate-100/90" : ""}
          >
            <DepartmentAssessmentBlock
              title={mod.title}
              payload={reportData?.departmentAssessments?.[mod.key]}
              loading={loading}
              formatTimestamp={formatTimestamp}
              compact={compact}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
